/* eslint-disable class-methods-use-this */

import React, {Component} from 'react';
import PropTypes from 'prop-types';

import LessonBlock from 'scripts/frontend/lessons/components/LessonBlock';
import LessonAbortModal from '../LessonAbortModal';
import Lesson from './Lesson';

/**
 * Main lesson component. Renders the current
 * block and the progress. Stores the answers.
 */
class LessonWrapper extends Component {

    static propTypes = {
        blocks: PropTypes.arrayOf(PropTypes.object).isRequired,
        onComplete: PropTypes.func.isRequired,
        cancelationURL: PropTypes.string,
        onAbort: PropTypes.func,
    };

    static defaultProps = {
        cancelationURL: null,
        onAbort: null,
    };

    constructor(props) {
        super(props);

        /* Bind this methods so subcomponents can override it and can access `this`. */
        this.isSuitableBlock = this.isSuitableBlock.bind(this);
        this.updateAnswer = this.updateAnswer.bind(this);
        this.onContinue = this.onContinue.bind(this);
        this.isRepeatable = this.isRepeatable.bind(this);
        this.handleAbort = this.handleAbort.bind(this);
        this.renderAbortModal = this.renderAbortModal.bind(this);

        this.state = {
            history: [],
            index: 0,
            blocks: props.blocks,
            answers: {},
            showAbortModal: false,
            missingInfo: null,
        };
    }

    /**
     * Returns true if the block provided can be repeated, else false.
     * Every block can be repeated by default. Lessons that handle additional blocks must override this
     * method in order to change it's behaviour.
     */
    isRepeatable() {
        return true;
    }

    renderAbortModal() {
        const {cancelationURL} = this.props;

        return <LessonAbortModal url={cancelationURL} onAbort={this.handleAbort} />;
    }

    /**
     * Renders the current block's intro text, the block's subcomponent,
     * the back/continue buttons and the progress.
     */
    render() {
        const {index, blocks, answers, missingInfo, notifyOptionalMissing} = this.state;

        return (
            <Lesson
                index={index}
                blocks={blocks}
                answers={answers}
                missingInfo={missingInfo}
                notifyOptionalMissing={notifyOptionalMissing}
                onBack={this.onBack}
                onContinue={this.onContinue}
                updateAnswer={this.updateAnswer}
                isRepeatable={this.isRepeatable}
                renderAbortModal={this.renderAbortModal}
            />
        );
    }

    /**
     * Sets the suitable blocks to the state before mount.
     */
    componentWillMount() {
        this.state.blocks = this.state.blocks.filter(this.isSuitableBlock);
    }

    /**
     * Filter method for blocks.
     * Can be overriden by subclasses.
     *
     * @returns {boolean}
     */
    isSuitableBlock(block) {
        return block.partner === 2;
    }

    /**
     * Handler for the block components.
     * Updates the answer of the current block.
     */
    updateAnswer(answer) {
        this.setState(state => {
            const {index, blocks, answers} = state;
            const block = blocks[index];

            if (block.type === 'group') {
                return {
                    answers: {
                        ...answers,
                        ...answer,
                    },
                };
            }

            return {
                answers: {
                    ...answers,
                    [block.id]: answer,
                },
            };
        });
    }

    /**
     * Goes to the previous block.
     */
    onBack = () => {
        const {history} = this.state;

        const [previous] = history.slice(-1);
        const rest = history.slice(0, -1);

        this.setState({
            index: previous,
            history: rest,
        });
    };

    nextBlockIndex = index => {
        const {blocks, answers} = this.state;

        // guard
        if (index === blocks.length - 1) {
            // we are already in the last block
            return null;
        }

        const {id, type, config} = blocks[index];

        let nextIndex = null;

        if (type === 'group') {
            const subBlockCount = blocks.filter(sub => sub.group === id).length;

            nextIndex = index + subBlockCount + 1;

            if (nextIndex >= blocks.length) return null;
        } else if (type === 'choice') {
            const {multiple, choices} = config;

            // guard
            if (multiple === true) {
                nextIndex = index + 1;
            } else {
                if (!answers[id]) {
                    nextIndex = index + 1;
                } else {
                    const selectedChoiceIndex = answers[id].choices[0];
                    const target = choices[selectedChoiceIndex].target;
                    if (target) {
                        nextIndex = blocks.findIndex(({id}) => id === target);
                    } else {
                        nextIndex = index + 1;
                    }
                }
            }
        } else {
            nextIndex = index + 1;
        }

        const nextBlock = blocks[nextIndex];

        if (nextBlock.type === 'jump') return this.getJumpTarget(nextBlock);

        return nextIndex;
    };

    getJumpTarget = jump => {
        const {blocks} = this.state;
        const target = jump.config.target;

        const nextBlock = blocks.find(({id}) => target === id);

        if (nextBlock.type === 'jump') {
            return this.getJumpTarget(nextBlock);
        }

        return nextBlock.order - 1;
    };

    /**
     * Goes to the next block. Completes the lesson on the last block.
     */
    onContinue() {
        // 1. Handle missing required values
        const missingInfo = this.getMissingInfo();
        if (missingInfo) {
            return this.setState({missingInfo});
        }
        this.setState({missingInfo: null});

        // 2. Handle missing optional values notification
        const optionalAnswerWithNoValue = this.optionalAnswerWithNoValue();
        if (optionalAnswerWithNoValue === true || Object.keys(optionalAnswerWithNoValue).length > 0) {
            if (this.state.notifyOptionalMissing === undefined) {
                return this.setState({notifyOptionalMissing: optionalAnswerWithNoValue});
            }
            if (this.state.notifyOptionalMissing !== undefined) {
                this.setState({notifyOptionalMissing: undefined});
            }
        } else {
            this.setState({notifyOptionalMissing: undefined});
        }

        const {index, answers} = this.state;

        const next = this.nextBlockIndex(index);

        if (next === null) {
            return this.props.onComplete(answers)
        }

        this.setState(state => ({
            history: [...state.history, index],
            index: this.nextBlockIndex(index),
        }), () => window.scrollTo(0, 0));
    }

    /**
     * @returns {boolean}
     */
    optionalAnswerWithNoValue() {
        const {blocks, index, answers} = this.state;
        const block = blocks[index];
        if (block.type === 'group') {
            const subBlocks = blocks.filter(subBlock => subBlock.group === block.id);
            const blockAnswers = subBlocks.map(subBlock => answers[subBlock.id]);
            const configs = subBlocks.map(block => block.config);
            const returnValue = {}
            blockAnswers.forEach((ans, idx) => {
                if (configs[idx].answer_optional === true && ans === undefined) {
                    returnValue[idx] = true;
                }
            });
            return returnValue;
        }
        const answer = answers[block.id];
        const answerIsOptional = block.config.answer_optional === true;
        return answerIsOptional && answer === undefined;
    }

    getMissingInfo() {
        const {blocks, index, answers} = this.state;
        const block = blocks[index];

        if (block.type === 'group') {
            const subBlocks = blocks.filter(subBlock => subBlock.group === block.id);
            const blockAnswers = subBlocks.map(subBlock => answers[subBlock.id] || {});
            const configs = subBlocks.map(block => block.config);
            return LessonBlock.canContinue(
                block.type,
                blockAnswers,
                configs,
                {types: subBlocks.map(block => block.type)},
            );
        }

        const answer = answers[block.id] || {};
        return LessonBlock.canContinue(block.type, answer, block.config);
    }

    handleAbort(reason) {
        const {onAbort} = this.props;
        const {answers} = this.state;

        return onAbort && onAbort(answers, reason);
    }
}

export default LessonWrapper;



// WEBPACK FOOTER //
// ./scripts/frontend/lessons/components/Lesson/index.js